import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import {
  Button,
  Form,
  H4,
  FieldRadioButton,
  IconHourGlassLine,
  IconArrowHead,
  IconReviewStar,
  IconQuestion,
} from '../..';

import Popup from 'reactjs-popup';

// Import modules from this directory
import css from './MonthlyHoursForm.module.css';

/**
 * The MonthlyHoursForm component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.formId] - The form id
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} [props.disabled] - Whether the form is disabled
 * @param {boolean} [props.ready] - Whether the form is ready
 * @param {Function} props.onSubmit - The submit function
 * @param {boolean} [props.invalid] - Whether the form is invalid
 * @param {boolean} [props.pristine] - Whether the form is pristine
 * @returns {JSX.Element}
 *
 *
 */

export const MonthlyHoursForm = props => (
  <FinalForm
    mutators={{ ...arrayMutators }}
    {...props}
    render={formRenderProps => {
      const {
        formId = 'MonthlyHoursForm',
        form: formApi,
        className,
        rootClassName,
        disabled,
        ready,
        handleSubmit,
        invalid,
        pristine,
        // initialValues: formInitialValues,
        values: formValues,
        intl,
      } = formRenderProps;

      const classes = classNames(rootClassName || css.root, className);
      const submitReady = ready;
      const submitDisabled = invalid || disabled;

      return (
        <Form onSubmit={handleSubmit} className={classes} id={formId}>
          <H4 className={css.title}>
            <FormattedMessage id="PackagesModal.MonthlyHoursForm.title" />
          </H4>
          <div className={css.inputWrapper}>
            <div className={css.label}>
              <FormattedMessage id="PackagesModal.MonthlyHoursForm.monthlyHoursLabel" />
            </div>
            <FieldRadioButton
              id={`${formId}-12hours`}
              name="monthlyTutoringHours"
              label={intl.formatMessage({
                id: 'PackagesModal.MonthlyHoursForm.12hours',
              })}
              className={css.monthlyTutoringHours}
              value="12"
              showAsRequired={true}
            />
            <FieldRadioButton
              id={`${formId}-8hours`}
              name="monthlyTutoringHours"
              label={
                <span className={css.monthlyTutoringHoursLabelWithInfo}>
                  <span>
                    {intl.formatMessage({ id: 'PackagesModal.MonthlyHoursForm.8hours' })}
                    <IconReviewStar className={css.iconStar} isFilled={true} />
                  </span>
                  <Popup
                    className={css.infoPopup}
                    trigger={
                      <span>
                        <IconQuestion className={css.iconInfo} />
                      </span>
                    }
                    position="top center"
                  >
                    <div>
                      {intl.formatMessage({ id: 'PackagesModal.MonthlyHoursForm.8hours.info' })}
                    </div>
                  </Popup>
                </span>
              }
              className={css.monthlyTutoringHours}
              textClassName={css.monthlyTutoringHoursLabelText}
              value="8"
              showAsRequired={true}
            />
            <FieldRadioButton
              id={`${formId}-4hours`}
              name="monthlyTutoringHours"
              label={intl.formatMessage({
                id: 'PackagesModal.MonthlyHoursForm.4hours',
              })}
              className={css.monthlyTutoringHours}
              value="4"
              showAsRequired={true}
            />
          </div>
          <div className={css.note}>
            <IconHourGlassLine className={css.hourGlassLineIcon} />
            <FormattedMessage id="PackagesModal.MonthlyHoursForm.note" />
          </div>
          <Button
            className={css.submitButton}
            type="submit"
            disabled={submitDisabled}
            ready={submitReady}
          >
            <FormattedMessage id="PackagesModal.MonthlyHoursForm.saveActionMsg" />
            <IconArrowHead direction="right" size="small" />
          </Button>
        </Form>
      );
    }}
  />
);

export default MonthlyHoursForm;
